/**
 * Zoom (wheel, around the cursor) and pan (drag) for the sheet preview, with
 * zoom buttons and a reset that puts the page back to fitting the stage.
 * Clicks still reach the content: a press only becomes a pan once it moves.
 */

import { ArrowsPointingInIcon, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useT } from '../../translation/src';

interface View {
  scale: number;
  x: number;
  y: number;
}

const FIT: View = { scale: 1, x: 0, y: 0 };
const MIN_SCALE = 0.25;
const MAX_SCALE = 16;
const WHEEL_STEP = 1.15;
const BUTTON_STEP = 1.5;
/** Pointer travel (px) before a press counts as a pan instead of a click. */
const DRAG_THRESHOLD = 3;

const clampScale = (s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

/** Zoom to `next` keeping the stage point `p` (relative to the stage center) where it is. */
function zoomAt(view: View, next: number, px: number, py: number): View {
  const scale = clampScale(next);
  const k = scale / view.scale;
  return { scale, x: px - k * (px - view.x), y: py - k * (py - view.y) };
}

interface PanZoomStageProps {
  children: React.ReactNode;
  /** A click (not a pan) that no content handled, e.g. beside the page. */
  onClick?: () => void;
  /** Room around the fitted content (Tailwind padding class). */
  paddingClassName?: string;
  /** Smaller zoom buttons for compact stages. */
  compact?: boolean;
}

/** Remount (change `key`) to start from the fitted view again. */
export const PanZoomStage: React.FC<PanZoomStageProps> = ({ children, onClick, paddingClassName = 'p-8', compact }) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>(FIT);
  const [panning, setPanning] = useState(false);
  const drag = useRef<{ pointerId: number; startX: number; startY: number; originX: number; originY: number; moved: boolean } | null>(null);
  const labels = { zoomIn: useT('zoom in', { swc: true }), zoomOut: useT('zoom out', { swc: true }), reset: useT('fit the page', { swc: true }) };

  // Wheel zoom needs a non-passive listener to keep the page from scrolling.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = stage.getBoundingClientRect();
      const px = e.clientX - rect.left - rect.width / 2;
      const py = e.clientY - rect.top - rect.height / 2;
      // Pinch gestures arrive as ctrl+wheel with small deltas: zoom proportionally.
      const factor = e.ctrlKey ? Math.exp(-e.deltaY * 0.01) : e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
      setView((v) => zoomAt(v, v.scale * factor, px, py));
    };
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0 && e.button !== 1) return;
    // A middle press would start the browser's autoscroll, which swallows the release.
    if (e.button === 1) e.preventDefault();
    drag.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, originX: view.x, originY: view.y, moved: false };
  };

  const endDrag = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    if (d.moved) stageRef.current?.releasePointerCapture(e.pointerId);
    setPanning(false);
    if (!d.moved) {
      drag.current = null;
      return;
    }
    // Keep `moved` for the click that follows this release (swallowed below), but only for that
    // event: a middle-button release or a lost pointerup sends no click, and the next real click must go through.
    setTimeout(() => {
      if (drag.current === d) drag.current = null;
    }, 0);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    // Released outside the window (or the up event got lost): stop panning.
    if (e.buttons === 0) {
      endDrag(e);
      return;
    }
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      d.moved = true;
      setPanning(true);
      stageRef.current?.setPointerCapture(e.pointerId);
    }
    setView((v) => ({ ...v, x: d.originX + dx, y: d.originY + dy }));
  };

  // A pan must not also click whatever is under the pointer when it ends.
  const onClickCapture = (e: React.MouseEvent) => {
    if (drag.current?.moved) {
      e.stopPropagation();
      e.preventDefault();
    }
    drag.current = null;
  };

  const zoomButton = (factor: number) => setView((v) => zoomAt(v, v.scale * factor, 0, 0));
  const isFit = view.scale === FIT.scale && view.x === FIT.x && view.y === FIT.y;

  return (
    <div
      ref={stageRef}
      // No text selection or native image drag on the page: a drag here always pans.
      className={classNames('absolute inset-0 touch-none select-none overflow-hidden', panning ? 'cursor-grabbing' : 'cursor-grab')}
      onPointerDown={onPointerDown}
      onMouseDown={(e) => e.button === 1 && e.preventDefault()}
      onAuxClick={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClickCapture={onClickCapture}
      onClick={onClick}
    >
      <div className={classNames('flex h-full w-full items-center justify-center', paddingClassName)} style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`, transformOrigin: 'center' }}>
        {children}
      </div>
      <div
        className={classNames(
          'absolute flex items-center gap-1 rounded-lg p-1 transition-opacity',
          // Compact stages are small: the controls stay see-through until the pointer is on them.
          compact
            ? 'bottom-1.5 right-1.5 origin-bottom-right scale-90 bg-black/25 text-white opacity-50 backdrop-blur-sm hover:bg-black/60 hover:opacity-100'
            : 'bottom-4 right-4 bg-white text-black shadow-md dark:bg-[#262626] dark:text-white',
        )}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="rounded p-1.5 hover:bg-black/10 dark:hover:bg-white/10" title={labels.zoomOut} aria-label={labels.zoomOut} onClick={() => zoomButton(1 / BUTTON_STEP)}>
          <MagnifyingGlassMinusIcon className="h-4 w-4" />
        </button>
        <span className="w-12 text-center text-xs tabular-nums">{Math.round(view.scale * 100)}%</span>
        <button type="button" className="rounded p-1.5 hover:bg-black/10 dark:hover:bg-white/10" title={labels.zoomIn} aria-label={labels.zoomIn} onClick={() => zoomButton(BUTTON_STEP)}>
          <MagnifyingGlassPlusIcon className="h-4 w-4" />
        </button>
        <button type="button" className="rounded p-1.5 hover:bg-black/10 disabled:opacity-40 dark:hover:bg-white/10" title={labels.reset} aria-label={labels.reset} disabled={isFit} onClick={() => setView(FIT)}>
          <ArrowsPointingInIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
